import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import SubTopicCard from '@/components/SubTopicCard';
import { useResources } from '@/hooks/useResourceContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, PlusCircle, Pen, Check, X, Image, AlertTriangle } from 'lucide-react';
import ImageEditor from '@/components/ImageEditor';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { SubTopic } from '@/types/resource';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { optimizeImage } from '@/utils/imageUtils';

const ResourceDetailsPage = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const { 
    getResourceById, 
    getCategoryById, 
    isAdmin, 
    updateResource,
    addResource,
    getDefaultResource,
    updateSubTopic,
    removeSubTopic,
    settings
  } = useResources();
  
  const resource = resourceId ? getResourceById(resourceId) : undefined;
  const category = resource ? getCategoryById(resource.categoryId) : undefined;
  
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedResource, setEditedResource] = useState(resource ? { ...resource } : undefined);
  
  const [isImageUploadDialogOpen, setIsImageUploadDialogOpen] = useState(false);
  const [currentSubtopic, setCurrentSubtopic] = useState<SubTopic | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [storageWarning, setStorageWarning] = useState(false);
  
  useEffect(() => {
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        totalSize += key.length + value.length;
      }
    }
    
    const sizeInMB = totalSize / (1024 * 1024);
    if (sizeInMB > 3) {
      setStorageWarning(true);
    }
  }, []);

  useEffect(() => {
    if (resource) {
      setEditedResource({ ...resource });
    }
  }, [resource]);

  if (!resource || !category) {
    const defaultResource = resourceId ? getDefaultResource(resourceId) : undefined;
    
    if (defaultResource) {
      if (isAdmin) {
        const handleAddDefaultResource = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          
          addResource(defaultResource);
          toast.success("Resource added to your collection");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        };
        
        return (
          <Layout>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Resource not found in your collection</h1>
              <p className="text-gray-600 mb-6">This is a default resource that hasn't been added to your collection yet.</p>
              <Button 
                onClick={handleAddDefaultResource}
                className="bg-brand-blue hover:bg-brand-darkBlue text-white"
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Add to my resources
              </Button>
            </div>
          </Layout>
        );
      }
    }
    
    return (
      <Layout>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-red-500">Resource not found</h1>
          <Button 
            onClick={() => navigate('/')}
            className="mt-4"
          >
            Return to Home
          </Button>
        </div>
      </Layout>
    );
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditedResource({ ...resource });
    setIsEditing(true);
  };

  const handleSaveResource = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (editedResource && resourceId) {
      updateResource(resourceId, editedResource);
      setIsEditing(false);
      toast.success("Resource updated successfully");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(false);
  };

  const handleAddSubTopic = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/resource/${resourceId}/add-subtopic`);
  };

  const handleEditSubTopic = (subtopic: SubTopic) => {
    console.log('Edit subtopic', subtopic);
    if (resourceId) {
      updateSubTopic(resourceId, subtopic.id, subtopic);
      toast.success(`Subtopic "${subtopic.title}" updated`);
    }
  };

  const handleDeleteSubTopic = (subtopic: SubTopic) => {
    if (resourceId) {
      if (window.confirm(`Are you sure you want to delete "${subtopic.title}"?`)) {
        removeSubTopic(resourceId, subtopic.id);
        toast.success(`Subtopic "${subtopic.title}" deleted`);
      }
    }
  };

  const handleEmbedCode = (subtopic: SubTopic) => {
    console.log('Embed code for subtopic', subtopic);
    const embedCode = prompt('Enter HTML embed code:');
    if (embedCode !== null && resourceId) {
      updateSubTopic(resourceId, subtopic.id, { 
        ...subtopic,
        htmlEmbed: embedCode 
      });
      toast.success('HTML code embedded successfully');
    }
  };

  const handleImageUpload = (subtopic: SubTopic) => {
    setCurrentSubtopic(subtopic);
    setImagePreviewUrl(subtopic.imageUrl || '');
    setIsImageUploadDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size exceeds 5MB. Please choose a smaller image.");
        return;
      }
      
      setSelectedImage(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setImagePreviewUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const optimizeImageForStorage = async (imageUrl: string): Promise<string> => {
    if (imageUrl.startsWith('data:image')) {
      return optimizeImage(imageUrl, 800, 0.7);
    }
    return imageUrl;
  };

  const handleSaveSubtopicImage = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (currentSubtopic && resourceId && imagePreviewUrl) {
      try {
        const optimizedImageUrl = await optimizeImageForStorage(imagePreviewUrl);
        
        updateSubTopic(resourceId, currentSubtopic.id, {
          ...currentSubtopic,
          imageUrl: optimizedImageUrl
        });
        
        setIsImageUploadDialogOpen(false);
        toast.success('Image added to subtopic');
        setSelectedImage(null);
      } catch (error) {
        console.error('Failed to save image:', error);
        toast.error('Failed to save image. Please try a smaller image or different format.');
      }
    } else {
      toast.error('Please select an image first');
    }
  };

  const handleExternalImageUrl = (url: string) => {
    if (url) {
      setImagePreviewUrl(url);
      setSelectedImage(null);
    }
  };

  const canEdit = settings.developerMode || isAdmin;

  return (
    <Layout>
      {storageWarning && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Storage Warning</AlertTitle>
          <AlertDescription>
            Your browser storage is nearly full. You may experience issues saving changes.
            Consider deleting unused resources or clearing your browser data.
          </AlertDescription>
        </Alert>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/category/${resource.categoryId}`)}
            className="text-brand-blue hover:text-brand-darkBlue"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to {category?.name}
          </Button>
          {canEdit && (
            <div className="flex space-x-2">
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowImageEditor(true);
                }}
                className="bg-brand-blue hover:bg-brand-darkBlue text-white"
              >
                <Image className="mr-2 h-4 w-4" /> Edit Main Image
              </Button>
              <Button 
                onClick={handleAddSubTopic}
                className="bg-brand-blue hover:bg-brand-darkBlue text-white"
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Add Sub-Topic
              </Button>
            </div>
          )}
        </div>
        
        <div className="h-64 rounded-lg overflow-hidden mb-6 relative">
          <img 
            src={resource.imageUrl} 
            alt={resource.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="mb-6">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input 
                value={editedResource?.title || ''}
                onChange={(e) => setEditedResource({ ...editedResource!, title: e.target.value })}
                className="text-3xl font-bold"
              />
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSaveResource}
                className="text-green-500"
              >
                <Check className="h-5 w-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleCancelEdit}
                className="text-red-500"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-800">{resource.title}</h1>
              {canEdit && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleEditClick}
                  className="ml-2 text-gray-500 hover:text-brand-blue"
                >
                  <Pen className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          {isEditing ? (
            <Textarea 
              value={editedResource?.description || ''}
              onChange={(e) => setEditedResource({ ...editedResource!, description: e.target.value })}
              className="mt-4"
            />
          ) : (
            <p className="text-gray-600 mt-2">{resource.description}</p>
          )}
        </div>
        
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Resource Topics</h2>
          {resource.subTopics && resource.subTopics.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {resource.subTopics.map(subTopic => (
                <SubTopicCard 
                  key={subTopic.id} 
                  subtopic={subTopic} 
                  resourceId={resource.id} 
                  resourceType={resource.type}
                  isAdmin={canEdit}
                  onEdit={handleEditSubTopic}
                  onDelete={handleDeleteSubTopic}
                  onEmbedCode={handleEmbedCode}
                  onImageUpload={handleImageUpload}
                />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No topics found for this resource.</p>
              {canEdit && (
                <Button 
                  onClick={handleAddSubTopic}
                  className="mt-4 bg-brand-blue hover:bg-brand-darkBlue text-white"
                >
                  <PlusCircle className="mr-2 h-4 w-4" /> Add your first topic
                </Button>
              )}
            </div>
          )}
        </div>
        
        {resource.link && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">External Resource</h2>
            <a 
              href={resource.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-brand-blue hover:text-brand-darkBlue underline flex items-center"
            >
              {resource.link} <ArrowLeft className="ml-2 h-4 w-4 transform rotate-135" />
            </a>
          </div>
        )}
      </div>
      
      {showImageEditor && (
        <ImageEditor
          isOpen={showImageEditor}
          onClose={() => setShowImageEditor(false)}
          onSave={async (newImageUrl) => {
            try {
              const optimizedImageUrl = await optimizeImageForStorage(newImageUrl);
              
              updateResource(resource.id, { ...resource, imageUrl: optimizedImageUrl });
              setShowImageEditor(false);
              toast.success("Resource image updated");
            } catch (error) {
              console.error('Failed to save image:', error);
              toast.error('Failed to save image. Please try a smaller image.');
            }
          }}
          originalImageUrl={resource.imageUrl}
          defaultImageUrl={resource.imageUrl}
          title="Edit Resource Image"
        />
      )}
      
      <Dialog 
        open={isImageUploadDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            setSelectedImage(null);
          }
          setIsImageUploadDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Topic Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="topic-image-upload">Upload Image</Label>
              <Input 
                id="topic-image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              
              <div className="mt-3">
                <Label htmlFor="external-url">Or enter image URL</Label>
                <div className="flex mt-1">
                  <Input 
                    id="external-url"
                    placeholder="https://example.com/image.jpg"
                    value={imagePreviewUrl}
                    onChange={(e) => setImagePreviewUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    className="ml-2"
                    onClick={() => handleExternalImageUrl(imagePreviewUrl)}
                  >
                    Preview
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start">
                <span className="text-blue-500 mr-2">ℹ️</span>
                <div className="text-sm text-blue-700">
                  <p><strong>Recommended image dimensions:</strong></p>
                  <ul className="list-disc ml-4 mt-1">
                    <li>Topic images: 800×600 pixels (4:3 aspect ratio)</li>
                    <li>Maximum file size: 5MB</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {imagePreviewUrl && (
              <div className="mt-4">
                <Label>Preview</Label>
                <div className="mt-2 border rounded-md overflow-hidden">
                  <img 
                    src={imagePreviewUrl} 
                    alt="Preview" 
                    className="w-full h-auto max-h-40 object-cover"
                    onError={() => toast.error("Invalid image URL")}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsImageUploadDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSubtopicImage} 
              className="bg-brand-blue hover:bg-brand-darkBlue"
            >
              Save Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ResourceDetailsPage;
